import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import Select, { type SelectProps } from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import StatusIndicator from '@amzn/awsui-components-core/status-indicator';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetWizardState } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import type { HierarchyChooser } from 'src/components/star-grid/datasets/hooks/useHierarchyChooser';
import type { ProfileColumn } from 'src/components/star-grid/datasets/datasetTypes';
import { COVERAGE_BLOCK, COVERAGE_WARN } from 'src/components/star-grid/datasets/datasetConstants';
import { columnHierarchyCandidates, effectiveHierarchyChoice, type HierarchyChoice } from 'src/components/star-grid/datasets/utils/datasetHierarchy';
import styles from 'src/components/star-grid/datasets/components/styles/DatasetWizard.module.scss';

export const FLAT = 'flat';
export const CHOOSE = '__choose__';
const hierarchyValue = (hierarchy: string): string => `h::${hierarchy}`;
const pct = (coverage: number): string => `${Math.round(coverage * 100)}%`;

interface Props {
  readonly state: DatasetWizardState;
  readonly text: StarGridText;
  readonly chooser: HierarchyChooser;
  readonly column: ProfileColumn;
}

/** One dimension's Prophecy hierarchy: the best match pre-selected with its coverage, changeable or clearable to flat. */
export const ColumnHierarchySelect: React.FC<Props> = ({ state, text, chooser, column }) => {
  const choice = effectiveHierarchyChoice(column, state.hierarchyChoices);
  const noRows = state.profile?.row_count === 0;

  // Candidates, the user's hand-picked pairs, and the choice in effect -- so a manual pick stays listed
  // even after switching to a candidate and back.
  const options: SelectProps.Option[] = [];
  const seen = new Set<string>();
  const push = (value: string, label: string): void => {
    if (seen.has(value)) return;
    seen.add(value);
    options.push({ value, label });
  };
  columnHierarchyCandidates(column).forEach((candidate) =>
    push(hierarchyValue(candidate.hierarchy), `${candidate.hierarchy} — ${pct(candidate.coverage)} ${text('wizardHierarchyCoverage')}`)
  );
  chooser.pickedFor(column.name).forEach((pair) => push(hierarchyValue(pair.hierarchy), pair.hierarchy));
  if (choice.kind === 'hierarchy') push(hierarchyValue(choice.hierarchy), choice.hierarchy);
  push(FLAT, text('wizardHierarchyFlat'));
  push(CHOOSE, text('wizardHierarchyChooseAnother'));

  // A candidate carries its coverage; a hand-picked or already-chosen pair carries none.
  const resolve = (hierarchy: string): HierarchyChoice | null => {
    const candidate = columnHierarchyCandidates(column).find((entry) => entry.hierarchy === hierarchy);
    if (candidate) return { kind: 'hierarchy', dimension: candidate.dimension, hierarchy, coverage: candidate.coverage };
    const picked = chooser.pickedFor(column.name).find((pair) => pair.hierarchy === hierarchy);
    if (picked) return { kind: 'hierarchy', dimension: picked.dimension, hierarchy, coverage: null };
    return choice.kind === 'hierarchy' && choice.hierarchy === hierarchy ? choice : null;
  };

  const onChange = (value: string | undefined): void => {
    if (value === FLAT) state.setHierarchyChoice(column.name, { kind: 'flat' });
    else if (value === CHOOSE) chooser.open(column.name);
    else if (value?.startsWith('h::')) {
      const resolved = resolve(value.slice(3));
      if (resolved) state.setHierarchyChoice(column.name, resolved);
    }
  };

  const selectedValue = choice.kind === 'flat' ? FLAT : hierarchyValue(choice.hierarchy);
  const coverage = choice.kind === 'hierarchy' ? choice.coverage : null;
  return (
    <SpaceBetween size="xxs">
      <Select
        expandToViewport
        ariaLabel={`${text('wizardMapHierarchy')} ${column.name}`}
        data-testid={`hierarchy-select-${column.name}`}
        options={options}
        selectedOption={options.find((option) => option.value === selectedValue) ?? null}
        onChange={({ detail }) => onChange(detail.selectedOption.value)}
      />
      {choice.kind === 'hierarchy' && coverage === null && (
        <Box variant="small">
          <span className={styles.samples}>{text('wizardHierarchyNotVerified')}</span>
        </Box>
      )}
      {coverage !== null && coverage < COVERAGE_BLOCK && (
        <StatusIndicator type="error" data-testid={`hierarchy-block-${column.name}`}>
          {Math.round((1 - coverage) * 100)}
          {text('wizardHierarchyDropBlock')}
        </StatusIndicator>
      )}
      {coverage !== null && coverage >= COVERAGE_BLOCK && coverage < COVERAGE_WARN && (
        <StatusIndicator type="warning" data-testid={`hierarchy-warn-${column.name}`}>
          {Math.round((1 - coverage) * 100)}
          {text('wizardHierarchyDropWarn')}
        </StatusIndicator>
      )}
      {choice.kind === 'hierarchy' && coverage === null && noRows && (
        <StatusIndicator type="info" data-testid={`hierarchy-no-rows-${column.name}`}>
          {text('wizardHierarchyNoRows')}
        </StatusIndicator>
      )}
    </SpaceBetween>
  );
};
